import * as React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import { Panes } from 'modules/core/components/Panes'
import PanesHeader from 'modules/core/components/PanesHeader'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import type { ModalStates } from 'modules/core/lib/questionModalStates'

import NewTemplateQuestion from 'modules/surveys/components/Questions/AddQuestion/NewQuestionConfigModal'
import NewQuestionsTemplateCategory from 'modules/surveys/components/Questions/Categories/NewCategoryModal'
import questionsTemplateCategories from 'modules/surveys/store/collections/questionsTemplateCategories'
import templateQuestions from 'modules/surveys/store/collections/templateQuestions'

import { useQuestionsTemplate } from '../questionsTemplateContext'
import TemplateQuestionsWithCategory from './WithCategory'
import TemplateQuestionsWithoutCategory from './WithoutCategory'
import type {
  CreateTemplateQuestion,
  DeleteTemplateQuestion,
  ReorderTemplateQuestion,
  UpdateTemplateQuestion,
} from './types'

interface Props {
  title: string
  description: string
  questionsTemplateId: number
}

function useTemplateQuestions(questionsTemplateId: number) {
  const { loading } = useAsyncData(async () => {
    await Promise.all([
      templateQuestions.fetch({
        data: {
          questions_template_id: questionsTemplateId,
        },
      }),
      questionsTemplateCategories.fetch({
        data: {
          questions_template_id: questionsTemplateId,
        },
      }),
    ])
  }, [])

  return {
    loading,
  }
}

const TemplateQuestionsList = ({ title, description, questionsTemplateId }: Props) => {
  const { loading } = useTemplateQuestions(questionsTemplateId)
  const [questionModal, setQuestionModal] = React.useState<ModalStates>('none')
  const [categoryModal, setCategoryModal] = React.useState<ModalStates>('none')
  const { isCore, isScore } = useQuestionsTemplate()

  const fetchTemplateQuestions = () =>
    templateQuestions.fetch({
      data: {
        questions_template_id: questionsTemplateId,
      },
    })

  const createTemplateQuestion = async (
    data: CreateTemplateQuestion,
    questionsTemplateCategoryId?: number
  ) => {
    const { success, value } = await asyncAction(() =>
      templateQuestions.create({
        questions_template_id: questionsTemplateId,
        questions_template_category_id: questionsTemplateCategoryId,
        custom_field: data,
      })
    )

    // We reorder all question configs in backend
    if (success) await fetchTemplateQuestions()

    return value
  }

  const createQuestionsTemplateCategory = async (data: Record<string, unknown>) => {
    const { value } = await asyncAction(() =>
      questionsTemplateCategories.create({
        questions_template_id: questionsTemplateId,
        name: data.name,
      })
    )

    return value
  }

  const updateTemplateQuestion = async (data: UpdateTemplateQuestion) => {
    const questionTemplate = templateQuestions.find({
      id: data.template_question_id,
    })

    if (!questionTemplate) {
      return
    }

    const { success, value } = await asyncAction(() => questionTemplate.save(data))

    // We reorder all question configs in backend
    if (success) await fetchTemplateQuestions()

    return value
  }

  const deleteTemplateQuestion = async (data: DeleteTemplateQuestion) => {
    const templateQuestion = templateQuestions.find({
      id: data.id,
    })

    if (!templateQuestion) {
      return
    }

    const { success, value } = await asyncAction(() => templateQuestion.destroy())

    // We reorder all question configs in backend
    if (success) await fetchTemplateQuestions()

    return value
  }

  const reorderTemplateQuestion = async (data: ReorderTemplateQuestion) => {
    const { success, value } = await asyncAction(() =>
      templateQuestions.rpc('reorder', {
        id: data.id,
        new_position: data.newPosition,
      })
    )

    // We reorder all question configs in backend
    if (success) await fetchTemplateQuestions()

    return value
  }

  const templateQuestionsWithoutCategories = templateQuestions.filter((templateQuestion) => {
    return templateQuestion.attributes.surveys_questions_template_category_id === null
  })

  return (
    <>
      <Panes direction="horizontal-2-cols-left-1-third" type="naked">
        <Box>
          <PanesHeader illustration="tasks_management" title={title} description={description} />
          {!isCore && !isScore && (
            <Box flexDirection="row" flexWrap="wrap">
              {/* This is a hack to be able to use Box and wrap the buttons in case they don't fit the width available */}
              <Box paddingBottom="s16" paddingRight="s16">
                <Button type="primary" onClick={() => setQuestionModal('new')}>
                  {i18n.t('surveys.form.question_configs.add_question_config')}
                </Button>
              </Box>
              <Button type="secondary" onClick={() => setCategoryModal('new')} iconName="add">
                {i18n.t('surveys.form.question_configs.add_section')}
              </Button>
            </Box>
          )}
        </Box>
        <Box paddingLeft="s32" gap="s24">
          {loading && <Loading />}
          {!loading && (
            <>
              {templateQuestionsWithoutCategories.length > 0 && (
                <TemplateQuestionsWithoutCategory
                  templateQuestions={templateQuestionsWithoutCategories}
                  deleteTemplateQuestion={deleteTemplateQuestion}
                  reorderTemplateQuestion={reorderTemplateQuestion}
                  createTemplateQuestion={createTemplateQuestion}
                  updateTemplateQuestion={updateTemplateQuestion}
                  isCore={isCore}
                />
              )}

              {questionsTemplateCategories.map((questionsTemplateCategory) => (
                <TemplateQuestionsWithCategory
                  key={`questionsTemplateCategory-${questionsTemplateCategory.id}`}
                  questionsTemplateCategory={questionsTemplateCategory}
                  createTemplateQuestion={createTemplateQuestion}
                  deleteTemplateQuestion={deleteTemplateQuestion}
                  reorderTemplateQuestion={reorderTemplateQuestion}
                  updateTemplateQuestion={updateTemplateQuestion}
                  isCore={isCore ?? false}
                />
              ))}
            </>
          )}
        </Box>
      </Panes>

      {questionModal === 'new' && (
        <NewTemplateQuestion
          onClose={() => setQuestionModal('none')}
          onSubmit={createTemplateQuestion}
        />
      )}

      {categoryModal === 'new' && (
        <NewQuestionsTemplateCategory
          onClose={() => setCategoryModal('none')}
          onSubmit={createQuestionsTemplateCategory}
        />
      )}
    </>
  )
}

export default observer(TemplateQuestionsList)
